/**
 * Storage Service
 * Resilient, safe LocalStorage wrapper with in-memory fallback for SSR, unit tests,
 * and environments with disabled or restricted storage.
 */

import { ThemeMode, ReminderItem, NoteItem, DailyGroup } from '../domain/types';

const STORAGE_KEYS = {
  THEME: 'zunelo_theme',
  REMINDERS: 'zunelo_reminders',
  NOTES: 'zunelo_notes',
  DAILY_CONFIG: 'zunelo_daily_config',
  DAILY_OVERRIDES: 'zunelo_daily_overrides',
  CRITERIA_OVERRIDES: 'zunelo_criteria_overrides',
  INSPECTOR_WIDTH: 'zunelo_inspector_width',
  RIGHT_WORKSPACE_SIDEBAR_WIDTH: 'zunelo_right_workspace_sidebar_width',
} as const;

class SafeStorage {
  private memoryStore = new Map<string, string>();

  private isLocalStorageAvailable(): boolean {
    try {
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      const testKey = '__zunelo_test__';
      window.localStorage.setItem(testKey, testKey);
      window.localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  public getItem<T>(key: string, fallback: T): T {
    try {
      let raw: string | null = null;
      if (this.isLocalStorageAvailable()) {
        raw = window.localStorage.getItem(key);
      } else {
        raw = this.memoryStore.get(key) ?? null;
      }

      if (raw === null || raw === undefined) {
        return fallback;
      }

      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  }

  public get<T>(key: string, fallback: T): T {
    return this.getItem<T>(key, fallback);
  }

  public setItem<T>(key: string, value: T): boolean {
    try {
      const serialized = JSON.stringify(value);
      if (this.isLocalStorageAvailable()) {
        window.localStorage.setItem(key, serialized);
      }
      this.memoryStore.set(key, serialized);
      return true;
    } catch {
      try {
        this.memoryStore.set(key, JSON.stringify(value));
      } catch {
        // Ignore memory serialization errors
      }
      return false;
    }
  }

  public set<T>(key: string, value: T): boolean {
    return this.setItem<T>(key, value);
  }

  public removeItem(key: string): boolean {
    try {
      if (this.isLocalStorageAvailable()) {
        window.localStorage.removeItem(key);
      }
      this.memoryStore.delete(key);
      return true;
    } catch {
      this.memoryStore.delete(key);
      return false;
    }
  }

  public clear(): void {
    try {
      if (this.isLocalStorageAvailable()) {
        window.localStorage.clear();
      }
      this.memoryStore.clear();
    } catch {
      this.memoryStore.clear();
    }
  }

  // --- Domain-Specific Helpers ---

  public getTheme(defaultTheme: ThemeMode = 'dark'): ThemeMode {
    return this.getItem<ThemeMode>(STORAGE_KEYS.THEME, defaultTheme);
  }

  public setTheme(theme: ThemeMode): void {
    this.setItem<ThemeMode>(STORAGE_KEYS.THEME, theme);
  }

  public getReminders(): ReminderItem[] | null {
    return this.getItem<ReminderItem[] | null>(STORAGE_KEYS.REMINDERS, null);
  }

  public saveReminders(reminders: ReminderItem[]): void {
    this.setItem<ReminderItem[]>(STORAGE_KEYS.REMINDERS, reminders);
  }

  public getNotes(): NoteItem[] | null {
    return this.getItem<NoteItem[] | null>(STORAGE_KEYS.NOTES, null);
  }

  public saveNotes(notes: NoteItem[]): void {
    this.setItem<NoteItem[]>(STORAGE_KEYS.NOTES, notes);
  }

  public getDailyConfig(): DailyGroup[] | null {
    return this.getItem<DailyGroup[] | null>(STORAGE_KEYS.DAILY_CONFIG, null);
  }

  public saveDailyConfig(config: DailyGroup[]): void {
    this.setItem<DailyGroup[]>(STORAGE_KEYS.DAILY_CONFIG, config);
  }

  public getDailyOverrides(): Record<string, boolean> {
    return this.getItem<Record<string, boolean>>(STORAGE_KEYS.DAILY_OVERRIDES, {});
  }

  public saveDailyOverrides(overrides: Record<string, boolean>): void {
    this.setItem<Record<string, boolean>>(STORAGE_KEYS.DAILY_OVERRIDES, overrides);
  }

  public getCriteriaOverrides(): Record<string, Record<string, boolean>> {
    return this.getItem<Record<string, Record<string, boolean>>>(
      STORAGE_KEYS.CRITERIA_OVERRIDES,
      {},
    );
  }

  public saveCriteriaOverrides(overrides: Record<string, Record<string, boolean>>): void {
    this.setItem<Record<string, Record<string, boolean>>>(
      STORAGE_KEYS.CRITERIA_OVERRIDES,
      overrides,
    );
  }

  public getInspectorWidth(): number | null {
    return this.getItem<number | null>(STORAGE_KEYS.INSPECTOR_WIDTH, null);
  }

  public saveInspectorWidth(width: number): void {
    this.setItem<number>(STORAGE_KEYS.INSPECTOR_WIDTH, width);
  }

  public getRightWorkspaceSidebarWidth(): number | null {
    return this.getItem<number | null>(STORAGE_KEYS.RIGHT_WORKSPACE_SIDEBAR_WIDTH, null);
  }

  public saveRightWorkspaceSidebarWidth(width: number): void {
    this.setItem<number>(STORAGE_KEYS.RIGHT_WORKSPACE_SIDEBAR_WIDTH, width);
  }
}

export const storageService = new SafeStorage();
export const StorageService = storageService;
export { SafeStorage, STORAGE_KEYS };
